import { supabase } from "@/lib/supabase-client";
import { nextSequentialNumber } from "@/lib/domain/numbering";
import { ExpenseType } from "@/types/db/expenses";
import { FileMetadata } from "@/types/files";

export const expenseService = {
  async getExpenses() {
    const { data, error } = await supabase
      .from("expenses")
      .select(
        `
        id,
        status,
        expense_number: meta_data->>expense_number,
        description,
        project_data: projects(id, name, status, meta_data, client: clients(id, name)),
        project_id,
        client_id,
        amount: meta_data->>amount,
        billing_items: meta_data->links->billing_items,
        shift_id: meta_data->>shift_id,
        created_at,
        user_id: meta_data->user_id
        `,
      )
      .order("created_at", { ascending: false });
    if (error) {
      throw Error(error.message);
    }

    // Collect unique shift IDs for enrichment
    const shiftIds = [...new Set(data?.map((exp: any) => exp.shift_id).filter(Boolean) || [])];
    const userIds = [...new Set(data?.map((exp: any) => exp.user_id).filter(Boolean) || [])];

    // Fetch shift names if there are any shift IDs
    let shiftMap: Record<string, string> = {};
    if (shiftIds.length > 0) {
      const { data: shifts } = await supabase.from("shifts").select("id, name").in("id", shiftIds);
      if (shifts) {
        shiftMap = Object.fromEntries(shifts.map((s) => [s.id, s.name]));
      }
    }

    let userMap: Record<string, string> = {};
    if (userIds.length > 0) {
      const { data: users } = await supabase
        .from("users")
        .select("id, first_name, last_name, preferred_name")
        .in("id", userIds);
      if (users) {
        userMap = Object.fromEntries(
          users.map((u) => [u.id, `${u.preferred_name ? u.preferred_name : u.first_name} ${u.last_name}`]),
        );
      }
    }

    // Transform to linked_record shapes
    return data?.map((exp: any) => {
      const pd = exp.project_data;
      return {
        // Project fields first for default column ordering
        project: pd ? { name: pd.name, id: pd.id } : null,
        project_status: pd?.status || null,
        billing_status: (pd?.meta_data as any)?.billing_status || null,
        ...exp,
        client: pd?.client ? { name: pd.client.name, id: pd.client.id } : null,
        shift: exp.shift_id ? { name: shiftMap[exp.shift_id] || null, id: exp.shift_id } : null,
        user: exp.user_id ? { name: userMap[exp.user_id] || null, id: exp.user_id } : null,
      };
    });
  },

  async getExpenseById(id: string): Promise<ExpenseType> {
    const { data, error } = await supabase.from("expenses").select("*").eq("id", id).single();
    if (error) {
      throw Error(error.message);
    }
    return data as ExpenseType;
  },

  async getExpensesByProjectId(projectId: string) {
    const { data, error } = await supabase
      .from("expenses")
      .select(
        `
        id,
        status,
        expense_number: meta_data->>expense_number,
        description,
        project_data: projects(id, name, status, meta_data, client: clients(id, name)),
        project_id,
        client_id,
        billing_items->meta_data->links->billing_items
        amount: meta_data->>amount,
        shift_id: meta_data->>shift_id,
        created_at
        `,
      )
      .eq("project_id", projectId)
      .order("created_at", { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch expenses for project ${projectId}: ${error.message}`);
    }

    // Collect unique shift IDs for enrichment
    const shiftIds = [...new Set(data?.map((exp: any) => exp.shift_id).filter(Boolean) || [])];

    let shiftMap: Record<string, string> = {};
    if (shiftIds.length > 0) {
      const { data: shifts } = await supabase.from("shifts").select("id, name").in("id", shiftIds);
      if (shifts) {
        shiftMap = Object.fromEntries(shifts.map((s) => [s.id, s.name]));
      }
    }

    return (data || []).map((exp: any) => {
      const pd = exp.project_data;
      return {
        project: pd ? { name: pd.name, id: pd.id } : null,
        project_status: pd?.status || null,
        billing_status: (pd?.meta_data as any)?.billing_status || null,
        ...exp,
        client: pd?.client ? { name: pd.client.name, id: pd.client.id } : null,
        shift: exp.shift_id ? { name: shiftMap[exp.shift_id] || null, id: exp.shift_id } : null,
      };
    });
  },

  async getExpensesByShiftId(shiftId: string) {
    const { data, error } = await supabase
      .from("expenses")
      .select(
        `
        id,
        status,
        expense_number: meta_data->>expense_number,
        description,
        project_data: projects(id, name, status, meta_data, client: clients(id, name)),
        project_id,
        client_id,
        amount: meta_data->>amount,
        shift_id: meta_data->>shift_id,
        created_at
        `,
      )
      .eq("meta_data->>shift_id", shiftId)
      .order("created_at", { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch expenses for shift ${shiftId}: ${error.message}`);
    }

    // Collect unique shift IDs for enrichment (the current one plus any others)
    const allShiftIds = [...new Set(data?.map((exp: any) => exp.shift_id).filter(Boolean) || [])];

    let shiftMap: Record<string, string> = {};
    if (allShiftIds.length > 0) {
      const { data: shifts } = await supabase.from("shifts").select("id, name").in("id", allShiftIds);
      if (shifts) {
        shiftMap = Object.fromEntries(shifts.map((s) => [s.id, s.name]));
      }
    }

    return (data || []).map((exp: any) => {
      const pd = exp.project_data;
      return {
        project: pd ? { name: pd.name, id: pd.id } : null,
        project_status: pd?.status || null,
        billing_status: (pd?.meta_data as any)?.billing_status || null,
        ...exp,
        client: pd?.client ? { name: pd.client.name, id: pd.client.id } : null,
        shift: exp.shift_id ? { name: shiftMap[exp.shift_id] || null, id: exp.shift_id } : null,
      };
    });
  },

  async addExpense(expense: Partial<ExpenseType> & { tenant_id: string }): Promise<ExpenseType> {
    const { data, error } = await supabase.from("expenses").insert([expense]).select().single();

    if (error) {
      throw Error(error.message);
    }
    return data as ExpenseType;
  },

  async updateExpense(id: string, patch: Partial<ExpenseType>): Promise<ExpenseType> {
    const { data, error } = await supabase.from("expenses").update(patch).eq("id", id).select().single();
    if (error) {
      throw new Error(error.message);
    }
    return data as ExpenseType;
  },

  async deleteExpense(id: string): Promise<boolean> {
    const { error } = await supabase.from("expenses").delete().eq("id", id);
    if (error) {
      throw Error(error.message);
    }
    return true;
  },

  async deleteExpensesByIds(ids: string[]): Promise<boolean> {
    const { error } = await supabase.from("expenses").delete().in("id", ids);
    if (error) {
      throw Error(error.message);
    }
    return true;
  },

  async saveRowPatches(patches: Partial<ExpenseType> & { id: string }[]): Promise<void> {
    await Promise.all(
      patches.map(async (patch) => {
        const { error } = await supabase.from("expenses").update(patch).eq("id", patch.id);
        if (error) throw new Error(error.message);
      }),
    );
  },

  // File management methods
  async uploadFile(expenseId: string, file: File, uploadedBy: string): Promise<FileMetadata> {
    // Get tenant_id from expense record
    const { data: tenantData, error: tenantError } = await supabase
      .from("expenses")
      .select("tenant_id")
      .eq("id", expenseId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get expense tenant: ${tenantError?.message || "Expense not found"}`);
    }

    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const filePath = `${tenantData.tenant_id}/${expenseId}/${fileId}-${file.name}`;

    // Upload file to storage
    const { error: uploadError } = await supabase.storage.from("expenses").upload(filePath, file);

    if (uploadError) {
      throw new Error(`Upload failed: ${uploadError.message}`);
    }

    // Create file metadata (store path, not URL)
    const fileMetadata: FileMetadata = {
      id: fileId,
      name: file.name,
      size: file.size,
      type: file.type,
      url: filePath, // Store path for signed URL generation
      bucket: "expenses", // Store bucket name
      uploadedAt: new Date().toISOString(),
      uploadedBy: uploadedBy,
    };

    // No database metadata tracking needed - files are listed directly from storage
    return fileMetadata;
  },

  async getFiles(expenseId: string): Promise<FileMetadata[]> {
    // Get tenant_id from expense record
    const { data: tenantData, error: tenantError } = await supabase
      .from("expenses")
      .select("tenant_id")
      .eq("id", expenseId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get expense tenant: ${tenantError?.message || "Expense not found"}`);
    }

    // List files from storage bucket
    const storagePath = `${tenantData.tenant_id}/${expenseId}`;
    const { data: files, error: listError } = await supabase.storage.from("expenses").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list expense files: ${listError.message}`);
    }

    if (!files || files.length === 0) {
      return [];
    }

    // Convert storage files to FileMetadata
    const fileMetadata: FileMetadata[] = files.map((file) => {
      // Parse fileId from filename (format: {fileId}-{filename})
      const dashIndex = file.name.indexOf("-");
      const fileId = dashIndex > 0 ? file.name.substring(0, dashIndex) : file.name;
      const fileName = dashIndex > 0 ? file.name.substring(dashIndex + 1) : file.name;

      return {
        id: fileId,
        name: fileName,
        size: file.metadata?.size || 0,
        type: file.metadata?.mimetype || "application/octet-stream",
        url: `${storagePath}/${file.name}`, // Store path for signed URL generation
        bucket: "expenses", // Store bucket name
        uploadedAt: file.created_at || new Date().toISOString(),
        uploadedBy: "unknown", // Storage doesn't track uploader, would need database for this
      };
    });

    return fileMetadata;
  },

  async deleteFile(expenseId: string, fileId: string): Promise<boolean> {
    // Get tenant_id from expense record
    const { data: tenantData, error: tenantError } = await supabase
      .from("expenses")
      .select("tenant_id")
      .eq("id", expenseId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get expense tenant: ${tenantError?.message || "Expense not found"}`);
    }

    // List files to find the one to delete
    const storagePath = `${tenantData.tenant_id}/${expenseId}`;
    const { data: files, error: listError } = await supabase.storage.from("expenses").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list expense files: ${listError.message}`);
    }

    const fileToDelete = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToDelete) {
      throw new Error("File not found in storage");
    }

    const filePath = `${storagePath}/${fileToDelete.name}`;
    const { error: storageError } = await supabase.storage.from("expenses").remove([filePath]);

    if (storageError) {
      throw new Error(`Failed to delete from storage: ${storageError.message}`);
    }

    return true;
  },

  async renameFile(expenseId: string, fileId: string, newName: string): Promise<FileMetadata> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("expenses")
      .select("tenant_id")
      .eq("id", expenseId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get expense tenant: ${tenantError?.message || "Expense not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${expenseId}`;
    const { data: files, error: listError } = await supabase.storage.from("expenses").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list expense files: ${listError.message}`);
    }

    const fileToRename = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToRename) {
      throw new Error("File not found in storage");
    }

    const oldPath = `${storagePath}/${fileToRename.name}`;
    const newPath = `${storagePath}/${fileId}-${newName}`;

    const { error: moveError } = await supabase.storage.from("expenses").move(oldPath, newPath);

    if (moveError) {
      throw new Error(`Failed to rename file: ${moveError.message}`);
    }

    return {
      id: fileId,
      name: newName,
      size: fileToRename.metadata?.size || 0,
      type: fileToRename.metadata?.mimetype || "application/octet-stream",
      url: newPath,
      bucket: "expenses",
      uploadedAt: fileToRename.created_at || new Date().toISOString(),
      uploadedBy: "unknown",
    };
  },

  async getNextExpenseNumber(): Promise<string> {
    // Fetch all expense numbers to find the highest and ensure uniqueness
    const { data, error } = await supabase.from("expenses").select("meta_data->expense_number");

    if (error) {
      console.error("Error fetching expenses for number generation:", error.message);
      return "1000";
    }

    return nextSequentialNumber((data || []).map((expense) => (expense as any)?.expense_number));
  },

  async getUnapprovedExpensesCount(): Promise<number> {
    const { data, error } = await supabase.from("expenses").select("id").neq("status", "APPROVED");

    if (error) {
      console.error("Error fetching unapproved expenses count:", error.message);
      return 0;
    }

    return data?.length ?? 0;
  },

  async getExpensesByShiftAndUser(shiftId: string, userId: string): Promise<ExpenseType[]> {
    const { data, error } = await supabase
      .from("expenses")
      .select("*")
      .eq("meta_data->>shift_id", shiftId)
      .eq("created_by", userId)
      .order("created_at", { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch expenses for shift: ${error.message}`);
    }

    return (data || []) as ExpenseType[];
  },
};
